import { useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import SiteHeader from "@/components/feature/SiteHeader";
import SiteFooter from "@/components/feature/SiteFooter";
import BackToTop from "@/components/feature/BackToTop";
import Breadcrumb from "@/components/base/Breadcrumb";
import PostCard from "@/components/base/PostCard";
import HotList from "@/components/feature/HotList";
import { useSeo } from "@/hooks/useSeo";
import { posts } from "@/mocks/posts";
import { categories } from "@/mocks/categories";

export default function Category() {
  const { slug = "all" } = useParams();
  const [sort, setSort] = useState<"latest" | "hot">("latest");

  const category = categories.find((c) => c.slug === slug);
  const isAll = slug === "all";
  const invalid = !isAll && !category;

  const catName = isAll ? "全部条目" : category?.name ?? "未知栏目";

  const list = useMemo(() => {
    const base = isAll ? posts : posts.filter((p) => p.categorySlug === slug);
    return [...base].sort((a, b) => (sort === "hot" ? b.heat - a.heat : b.date.localeCompare(a.date)));
  }, [slug, sort, isAll]);

  useSeo({
    title: invalid
      ? "未找到栏目 - 网曝吃瓜"
      : `${catName} - 爆料线索整理与投稿 | 网曝吃瓜`,
    description: invalid
      ? "该栏目暂不存在，请返回首页浏览其他爆料线索。"
      : `${category?.longDesc ?? "网曝吃瓜全部条目，集中展示网友投稿、爆料线索与素材原帖的整理结果。"}（网曝吃瓜 · 网友投稿聚合）`,
    keywords: `${catName},吃瓜爆料,网友投稿,吃瓜线索,网曝吃瓜`,
    ogType: "website",
    jsonLd: [
      {
        "@context": "https://schema.org",
        "@type": "CollectionPage",
        name: `${catName} - 网曝吃瓜`,
        description: category?.longDesc ?? "网曝吃瓜全部条目整理。",
        inLanguage: "zh-CN",
      },
      {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "首页", item: "https://wangbaochigua.example/" },
          { "@type": "ListItem", position: 2, name: catName },
        ],
      },
      {
        "@context": "https://schema.org",
        "@type": "ItemList",
        name: catName,
        itemListElement: list.slice(0, 10).map((p, i) => ({
          "@type": "ListItem",
          position: i + 1,
          url: `https://wangbaochigua.example/post/${p.slug}`,
          name: p.title,
        })),
      },
    ],
  });

  return (
    <div className="min-h-screen flex flex-col bg-background-50">
      <SiteHeader />
      <main className="flex-1">
        {/* 栏目头部 */}
        <section className="w-full border-b border-background-200 bg-background-100">
          <div className="w-full px-4 md:px-6 py-8 md:py-12">
            <Breadcrumb items={[{ label: isAll ? "全部条目" : catName }]} />
            <div className="mt-5 flex flex-col lg:flex-row lg:items-end justify-between gap-5">
              <div>
                <h1 className="text-3xl md:text-4xl font-bold text-foreground-950 flex items-center gap-3">
                  {category && (
                    <span className="w-11 h-11 flex items-center justify-center rounded-md bg-secondary-500 text-background-50">
                      <i className={`${category.icon} text-2xl`} aria-hidden="true" />
                    </span>
                  )}
                  {catName}
                </h1>
                <p className="mt-3 text-sm md:text-base text-foreground-600 leading-relaxed max-w-2xl">
                  {invalid
                    ? "没有找到对应的栏目，你可以从下面的全部条目继续浏览。"
                    : category?.longDesc ?? "这里汇总了网曝吃瓜全部在架条目，按时间与热度排序，方便你快速定位想看的线索。"}
                </p>
              </div>
              <div className="flex items-center gap-1 bg-background-50 p-1 rounded-full border border-background-200 w-max">
                {[
                  { key: "latest" as const, label: "最新整理" },
                  { key: "hot" as const, label: "热度优先" },
                ].map((item) => (
                  <button
                    key={item.key}
                    type="button"
                    onClick={() => setSort(item.key)}
                    className={`px-4 py-2 rounded-full text-sm whitespace-nowrap transition-colors cursor-pointer ${
                      sort === item.key ? "bg-foreground-950 text-background-50" : "text-foreground-600 hover:text-foreground-900"
                    }`}
                  >
                    {item.label}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </section>

        <div className="w-full px-4 md:px-6 py-8 md:py-12 grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-10">
          <div className="lg:col-span-8">
            <p className="mb-5 text-xs text-foreground-500">
              共 <span className="text-primary-600 font-medium">{list.length}</span> 条条目
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 md:gap-6">
              {list.map((post) => (
                <PostCard
                  key={post.id}
                  post={post}
                  categoryName={categories.find((c) => c.slug === post.categorySlug)?.name ?? "综合"}
                />
              ))}
            </div>
          </div>

          <aside className="lg:col-span-4 flex flex-col gap-6">
            <div className="lg:sticky lg:top-24 flex flex-col gap-6">
              <HotList posts={[...posts].sort((a, b) => b.heat - a.heat)} limit={8} />

              <section aria-labelledby="other-cat-title" className="bg-background-50 border border-background-200 rounded-lg p-4">
                <h2 id="other-cat-title" className="text-sm font-bold text-foreground-900 mb-3">
                  其他栏目
                </h2>
                <ul className="flex flex-wrap gap-2">
                  <li>
                    <Link
                      to="/category/all"
                      className={`px-3 py-1.5 rounded-md text-xs whitespace-nowrap transition-colors ${
                        isAll ? "bg-primary-500 text-background-50" : "bg-background-100 text-foreground-700 hover:bg-background-200"
                      }`}
                    >
                      全部条目
                    </Link>
                  </li>
                  {categories.map((c) => (
                    <li key={c.slug}>
                      <Link
                        to={`/category/${c.slug}`}
                        className={`px-3 py-1.5 rounded-md text-xs whitespace-nowrap transition-colors ${
                          c.slug === slug
                            ? "bg-primary-500 text-background-50"
                            : "bg-background-100 text-foreground-700 hover:bg-background-200"
                        }`}
                      >
                        {c.name}
                      </Link>
                    </li>
                  ))}
                </ul>
              </section>

              <section className="rounded-lg bg-secondary-950 p-5 text-background-100">
                <h2 className="text-sm font-bold flex items-center gap-2">
                  <i className="ri-send-plane-line text-accent-400" aria-hidden="true" />
                  有线索想分享？
                </h2>
                <p className="mt-2 text-sm text-secondary-200 leading-relaxed">
                  不需要实名，把过程讲清楚就好。编辑部会在 24 小时内完成初筛。
                </p>
                <Link
                  to="/submit"
                  className="mt-4 inline-flex items-center gap-2 px-4 py-2 rounded-md bg-accent-500 text-foreground-950 text-sm whitespace-nowrap hover:bg-accent-400 transition-colors"
                >
                  前往投稿
                </Link>
              </section>
            </div>
          </aside>
        </div>
      </main>
      <SiteFooter />
      <BackToTop />
    </div>
  );
}